import { useMemo, useState } from "react";
import { useModal } from "../../hooks/useModal";
import { useProducts } from "../../context/ProductsContext";
import { useProductOperations } from "../../hooks/inventario/useProductOperations";

import { CustomDataTable } from "../Tables/CustomDataTable";
import { InventarioAddImage } from "./InventarioAddImage";
import { CustomConfirm } from "../Modals/CustomConfirm";
import { icons } from "../../const/icons";
import "./InventarioContent.css";

/**
 * Vista de productos para el tab "Items"
 * Muestra tabla simple de productos con acciones básicas
 */
export const InventarioContent = () => {
  const { filteredProducts, pagination, isSearchMode, searchTerm } = useProducts();
  const { operations: productOperations, states: productStates } = useProductOperations();

  const { isOpen, open, close } = useModal();
  const [successVisible, setSuccessVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  //* DEFINICIÓN DE COLUMNAS
  const columns = useMemo(
    () => [
      {
        title: "Ítem",
        dataIndex: "sku",
        key: "sku",
        width: "10%",
        sorter: (a, b) => a.sku.localeCompare(b.sku),
        render: (text) => <span className="item-sku-cell">{text}</span>,
        onCell: () => ({
          style: { borderRight: "2px dashed #d1d5db" },
        }),
      },
      {
        title: "Producto",
        dataIndex: "product",
        key: "product",
        width: "auto",
        render: (text) => <span>{text}</span>,
      },
      {
        title: "Imagen",
        dataIndex: "imageUrl",
        key: "imageUrl",
        width: "10%",
        render: (text, record) => (
          <button
            type="button"
            className="item-mini-image-wrapper"
            onClick={(e) => handleOpenImage(e, record)}
            aria-label={`Abrir imagen de ${text || "item"}`}
          >
            <img
              src={record.imageUrl || "/placeholder.png"}
              alt={text}
              className="item-mini-image"
              onError={(e) => {
                e.target.src = "/placeholder.png";
              }}
            />
          </button>
        ),
      },
      {
        title: "Unidad de medida base (UMB)",
        dataIndex: "umb",
        key: "umb",
        width: "auto",
        align: "center",
        // sorter: (a, b) => a.UMB - b.UMB,
        render: (cantidad) => <span className="item-cell">{cantidad}</span>,
      },
      // {
      //   title: "Unidad de medida de pedido (UMP)",
      //   dataIndex: "ump",
      //   key: "ump",
      //   width: "15%",
      //   align: "center",
      //   render: (cantidad) => <span className="item-cell">{cantidad}</span>,
      // },
      // {
      //   title: "Cantidad de UMP", //todo: no llega el valor
      //   dataIndex: "cantidadUMP",
      //   key: "cantidadUMP",
      //   width: "15%",
      //   align: "center",
      //   render: (cantidad) => <span className="item-cell">{cantidad}</span>,
      // },
      // {
      //   title: "Cantidad de UMB en UMP",
      //   dataIndex: "umbToUMP",
      //   key: "umbToUMP",
      //   width: "15%",
      //   align: "center",
      //   render: (value) => <span className="item-cell">{value}</span>,
      // },
    ],
    []
  );

  //* HANDLERS

  const handleTablePageChange = async (page, pageSize) => {
    const backendPage = page - 1;

    if (isSearchMode && searchTerm) {
      await productOperations.searchProducts(searchTerm, { page: backendPage, size: pageSize });
    } else {
      await productOperations.fetchProducts({ page: backendPage, size: pageSize });
    }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
  };

  const handleOpenImage = (e, product) => {
    e.stopPropagation();
    handleSelectProduct(product);
    open();
  };

  const handleSaveImage = async ({ productId, fileName, base64, contentType }) => {
    const res = await productOperations.updateProductImage(productId, {
      fileName,
      base64,
      contentType,
    });

    return res;
  };

  const handleDeleteImage = async (productId) => {
    try {
      const response = await productOperations.deleteProductImage(productId);
      if (response.error) {
        throw new Error(response.error);
      }
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, error: error.message || "Error al eliminar la imagen" };
    }
  };

  const handleImageSavedSuccessfully = () => {
    setSuccessVisible(true);
  };

  return (
    <>
      <div className="inventario-layout">
        <div className="inventario-table-container">
          <CustomDataTable
            data={filteredProducts}
            columns={columns}
            loading={productStates.fetchState.loading}
            onRowClick={handleSelectProduct}
            selectedRow={selectedProduct}
            showPagination
            pagination={{
              current: pagination.pageNumber + 1,
              pageSize: pagination.pageSize,
              total: pagination.totalElements,
            }}
            onPageChange={handleTablePageChange}
            emptyText="No se encontraron productos."
          />
        </div>
      </div>

      <InventarioAddImage
        visible={isOpen}
        onClose={close}
        producto={selectedProduct}
        onSubmit={handleSaveImage}
        onDelete={handleDeleteImage}
        saving={productStates.imageState.loading}
        serverError={productStates.imageState.error}
        onSuccess={handleImageSavedSuccessfully}
      />

      {/* MODAL DE ÉXITO AL GUARDAR IMAGEN */}
      <CustomConfirm
        visible={successVisible}
        caption="Guardado exitoso"
        description="La imagen se ha guardado correctamente."
        iconUrl={icons.check}
        okText="Aceptar"
        showCancel={false}
        confirmLoading={false}
        onConfirm={() => setSuccessVisible(false)}
        onCancel={() => setSuccessVisible(false)}
      />
    </>
  );
};
